/**
 * Interface for components that can use the backdrop
 */
export interface BackdropClient {
  useBackdrop: boolean;
  open: boolean;
  zIndex?: number;
}

/**
 * Singleton service to manage modal backdrops globally.
 * Ensures only one backdrop exists and handles z-index stacking for multiple modals.
 */
export class BackdropManager {
  private static instance: BackdropManager;
  private backdrop: HTMLElement | null = null;
  private activeModals: Set<BackdropClient> = new Set();

  // Bound event handler to prevent memory leaks
  private boundHandleBackdropClick = this.handleBackdropClick.bind(this);

  /**
   * CSS styles for the backdrop element with proper typing
   */
  private readonly backdropStyles: Partial<CSSStyleDeclaration> = {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(6px)',
    pointerEvents: 'auto',
    transition: 'opacity 0.2s ease-in-out',
  };

  /**
   * Private constructor to enforce singleton pattern
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * Get the singleton instance of BackdropManager
   */
  static getInstance(): BackdropManager {
    if (!BackdropManager.instance) {
      BackdropManager.instance = new BackdropManager();
    }
    return BackdropManager.instance;
  }

  /**
   * Register a modal component with the backdrop manager
   */
  addModal(modal: BackdropClient): void {
    this.activeModals.add(modal);
    this.updateBackdrop();
  }

  /**
   * Unregister a modal component from the backdrop manager
   */
  removeModal(modal: BackdropClient): void {
    this.activeModals.delete(modal);
    this.updateBackdrop();
  }

  /**
   * Force an update of the backdrop state
   */
  updateBackdrop(): void {
    const needsBackdrop = this.shouldShowBackdrop();

    if (needsBackdrop) {
      if (!this.backdrop) {
        this.createBackdrop();
      }
      this.updateBackdropZIndex();
    } else if (this.backdrop) {
      this.removeBackdrop();
    }
  }

  /**
   * Get the current backdrop element if it exists
   */
  getBackdrop(): HTMLElement | null {
    return this.backdrop;
  }

  /**
   * Check if any modal requires a backdrop
   */
  private shouldShowBackdrop(): boolean {
    return Array.from(this.activeModals).some((modal) => modal.useBackdrop && modal.open);
  }

  /**
   * Create and append the backdrop element to the document body
   */
  private createBackdrop(): void {
    if (this.backdrop) return;

    this.backdrop = document.createElement('div');
    this.backdrop.className = 'luds-modal-backdrop';
    this.backdrop.setAttribute('data-backdrop', 'true');

    // Apply styles
    Object.assign(this.backdrop.style, this.backdropStyles);

    // Add click handler using bound function to prevent memory leaks
    this.backdrop.addEventListener('click', this.boundHandleBackdropClick);

    // Append to body with fade-in effect
    this.backdrop.style.opacity = '0';
    document.body.appendChild(this.backdrop);

    // Trigger fade-in
    requestAnimationFrame(() => {
      if (this.backdrop) {
        this.backdrop.style.opacity = '1';
      }
    });
  }

  /**
   * Remove the backdrop element from the document
   */
  private removeBackdrop(): void {
    if (!this.backdrop) return;

    // Fade out before removing
    this.backdrop.style.opacity = '0';

    setTimeout(() => {
      if (this.backdrop) {
        // Remove event listener using the same bound function reference
        this.backdrop.removeEventListener('click', this.boundHandleBackdropClick);
        this.backdrop.remove();
        this.backdrop = null;
      }
    }, 200); // Match transition duration
  }

  /**
   * Update the z-index of the backdrop based on active modals
   */
  private updateBackdropZIndex(): void {
    if (!this.backdrop) return;

    const openModals = Array.from(this.activeModals).filter((m) => m.open && m.useBackdrop);

    if (openModals.length === 0) return;

    // Find the highest z-index among open modals with backdrops
    const zIndexes = openModals.map((m) => m.zIndex || 1000).filter((z) => z > 0);

    if (zIndexes.length === 0) return;

    const highestZIndex = Math.max(...zIndexes);

    // Set backdrop z-index to be just below the highest modal
    this.backdrop.style.zIndex = String(highestZIndex - 1);
  }

  /**
   * Handle click events on the backdrop
   * Dispatches a custom event that modals can listen to for close-on-backdrop-click functionality
   */
  private handleBackdropClick(event: Event): void {
    // Prevent click from bubbling to elements behind the backdrop
    event.stopPropagation();

    // Dispatch a custom event that modals can listen to
    const customEvent = new CustomEvent('backdrop-click', {
      bubbles: true,
      composed: true,
      detail: {
        backdrop: this.backdrop,
        timestamp: Date.now(),
      },
    });

    document.dispatchEvent(customEvent);
  }

  /**
   * Clean up all backdrops (useful for testing or cleanup scenarios)
   */
  destroy(): void {
    this.activeModals.clear();
    if (this.backdrop) {
      this.backdrop.removeEventListener('click', this.boundHandleBackdropClick);
      this.backdrop.remove();
      this.backdrop = null;
    }
  }

  /**
   * Get the count of active modals with backdrops
   */
  getActiveModalCount(): number {
    return Array.from(this.activeModals).filter((m) => m.open && m.useBackdrop).length;
  }

  /**
   * Check if a specific modal is registered
   */
  hasModal(modal: BackdropClient): boolean {
    return this.activeModals.has(modal);
  }

  /**
   * Get all registered modals (useful for debugging)
   */
  getRegisteredModals(): BackdropClient[] {
    return Array.from(this.activeModals);
  }

  /**
   * Check if backdrop is currently visible
   */
  isBackdropVisible(): boolean {
    return this.backdrop !== null;
  }
}
